import Cookies from 'js-cookie';
import { getBaseUrl } from '../utils/apiHelper';

const API_URL = `${getBaseUrl()}/api/auth`;

const authService = {
    register: async (email, password, username, smartBillEntry) => {
        const requestBody = { email, password, username, forwardEmail: smartBillEntry };
        console.log('[REGISTER DEBUG] Sending registration request:', requestBody);
        const res = await fetch(`${API_URL}/register`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(requestBody),
        });
        console.log('[REGISTER DEBUG] Raw response:', res);
        let data;
        try {
            data = await res.json();
        } catch (e) {
            console.error('[REGISTER DEBUG] Failed to parse JSON:', e);
            throw new Error('Invalid server response');
        }
        if (!res.ok) {
            if (data.errors) {
                const err = new Error('Registration validation failed');
                err.errors = data.errors;
                throw err;
            }
            throw new Error(data.message || 'Registration failed');
        }
        console.log('[REGISTER DEBUG] Registration success:', data);
        // Do NOT set authentication cookies for new registration
        // Only set cookies after successful login
        return data;
    },
    login: async (email, password) => {
        try {
            const response = await fetch(`${API_URL}/login`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email, password }),
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || "Login failed");
            }
            // Set authentication cookies (access token and refresh token)
            Cookies.set("token", data.token);
            Cookies.set("refreshToken", data.refreshToken);
            Cookies.set("userId", data.userId);
            Cookies.set("username", data.name); // Save username (email) in cookies
            Cookies.set("isAdmin", data.isAdmin ? "true" : "false"); // Save admin status as a string
            console.log("✅ Login successful. Cookies set:", {
                token: data.token,
                refreshToken: data.refreshToken,
                userId: data.userId,
                username: email,
                isAdmin: data.isAdmin ? "true" : "false",
            });
            return data.token; // Return the access token for use in subsequent requests
        } catch (error) {
            console.error("❌ Error during login:", error);
            throw error;
        }
    },
    logout: () => {
        // Clear the token, refreshToken, userId, username, and isAdmin from cookies
        Cookies.remove("token");
        Cookies.remove("refreshToken");
        Cookies.remove("userId");
        Cookies.remove("username");
        Cookies.remove("isAdmin");
        console.log("User logged out");
    },
    // Centralized safe logout with redirect reason (prevents duplicate redirects)
    forceLogout: (reason = 'session_expired') => {
        if (window.__isForcingLogout) return; // already in progress
        window.__isForcingLogout = true;
        try {
            authService.logout();
        } finally {
            // Use replace to avoid back button returning to protected page
            window.location.replace(`/login?reason=${encodeURIComponent(reason)}`);
        }
    },
    // Method to get the token from cookies
    getToken: () => {
        return Cookies.get("token");
    },
    // Method to get the userId from cookies
    getUserId: () => {
        return Cookies.get("userId");
    },
    // Method to get the username from cookies
    getUsername: () => {
        return Cookies.get("username");
    },
    // Method to get the refresh token from cookies
    getRefreshToken: () => {
        return Cookies.get("refreshToken");
    },
    
    // Method to refresh access token using refresh token
    refreshToken: async () => {
        try {
            const refreshToken = Cookies.get("refreshToken");
            if (!refreshToken) {
                throw new Error("No refresh token available");
            }

            const response = await fetch(`${API_URL}/refresh-token`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ refreshToken }),
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || "Token refresh failed");
            }

            // Update cookies with new tokens
            Cookies.set("token", data.token);
            Cookies.set("refreshToken", data.refreshToken);
            
            console.log("✅ Token refreshed successfully");
            return data.token;
        } catch (error) {
            console.error("❌ Error refreshing token:", error);
            // If refresh fails, redirect to login
            authService.logout();
            window.location.href = '/login';
            throw error;
        }
    },

    // Method to make authenticated requests with automatic token refresh
    authenticatedFetch: async (url, options = {}) => {
        const start = Date.now();
        let token = Cookies.get('token');
        const userId = Cookies.get('userId');
        if (!token) {
            const refreshToken = Cookies.get('refreshToken');
            if (refreshToken) {
                try {
                    console.log('🔄 authenticatedFetch: missing access token, attempting silent refresh');
                    token = await authService.refreshToken();
                } catch (e) {
                    console.warn('❌ Silent refresh failed (missing token path)');
                }
            }
        }
        if (!token) {
            const err = new Error('Session expired. Please log in again.');
            err.code = 'SESSION_EXPIRED';
            authService.forceLogout('session_expired');
            if (!window.__isForcingLogout) {
                authService.logout();
                window.location.replace('/login?reason=session_expired');
            }
            throw err;
        }
        const baseHeaders = { ...options.headers, 'Authorization': `Bearer ${token}` };
        if (userId) { baseHeaders['X-User-ID'] = userId; baseHeaders['userId'] = userId; }
        let response;
        try {
            response = await fetch(url, { ...options, headers: baseHeaders });
        } catch (networkErr) {
            console.error('🌐 Network error during fetch:', networkErr);
            networkErr.code = 'NETWORK_ERROR';
            networkErr.userMessage = 'Network issue. Please check your connection and log in again if needed.';
            authService.forceLogout('network');
            throw networkErr;
        }
        if (response.status === 401) {
            console.warn('⚠️ 401 received. Attempting single refresh...');
            try {
                token = await authService.refreshToken();
                if (!token) throw new Error('No token after refresh');
                const retryHeaders = { ...options.headers, 'Authorization': `Bearer ${token}` };
                if (userId) { retryHeaders['X-User-ID'] = userId; retryHeaders['userId'] = userId; }
                response = await fetch(url, { ...options, headers: retryHeaders });
            } catch (refreshErr) {
                console.error('❌ Refresh attempt failed:', refreshErr);
                const err = new Error('Session expired. Please log in again.');
                err.code = 'SESSION_EXPIRED';
                authService.forceLogout('session_expired');
                throw err;
            }
            if (response.status === 401) {
                console.warn('⚠️ Still 401 after single refresh attempt. Forcing logout.');
                const err = new Error('Session expired. Please log in again.');
                err.code = 'SESSION_EXPIRED';
                authService.forceLogout('session_expired');
                throw err;
            }
        }
        const duration = Date.now() - start;
        if (duration > 30000) {
            console.warn('⏱️ Slow request (>30s) triggering soft session check.');
        }
        return response;
    },
    
    // Method to handle Firebase Google auth
    firebaseGoogleLogin: async () => {
        try {
            // This function will be implemented in Login.js
            // as it requires UI interaction with Firebase popup
            console.log("Firebase Google login should be triggered from component");
        } catch (error) {
            console.error("❌ Error during Google login:", error);
            throw error;
        }
    },
    
    // Method for server-side Google OAuth flow (legacy)
    googleLogin: () => {
        // Redirect to backend Google authentication route
        window.location.href = `${API_URL}/google`;
    },
    
    // Method to handle Google auth result from Firebase
    handleFirebaseGoogleAuth: async (credential) => {
        try {
            // Send the Firebase ID token and smartBillEntry to the backend for verification
            const response = await fetch(`${API_URL}/firebase-google-auth`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ 
                    idToken: credential.idToken,
                    email: credential.email,
                    name: credential.displayName,
                    photoURL: credential.photoURL,
                    forwardEmail: credential.smartBillEntry // Pass Smart Bill Entry
                }),
            });
            
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || "Google authentication failed");
            }
            
            // Set authentication cookies
            Cookies.set("token", data.token);
            Cookies.set("refreshToken", data.refreshToken);
            Cookies.set("userId", data.userId);
            Cookies.set("username", data.name);
            Cookies.set("email", data.email);
            if (data.isAdmin !== undefined) {
                Cookies.set("isAdmin", data.isAdmin ? "true" : "false");
            }
            
            console.log("✅ Firebase Google login successful. Cookies set:", {
                token: data.token,
                refreshToken: data.refreshToken,
                userId: data.userId,
                username: data.name
            });
            
            return data;
        } catch (error) {
            console.error("❌ Error during Firebase Google auth:", error);
            throw error;
        }
    },
    
    // Method to handle the OAuth callback data
    handleOAuthCallback: (token, refreshToken, userId, name, email) => {
        // Set authentication cookies
        Cookies.set("token", token);
        Cookies.set("refreshToken", refreshToken);
        Cookies.set("userId", userId);
        Cookies.set("username", name);
        Cookies.set("email", email);
        console.log("✅ Google OAuth login successful. Cookies set:", {
            token, refreshToken, userId, username: name, email
        });
    },
    // Check if we're on the OAuth callback page and process data if so
    processOAuthCallback: () => {
        const params = new URLSearchParams(window.location.search);
        const token = params.get('token');
        const refreshToken = params.get('refreshToken');
        const userId = params.get('userId');
        const name = params.get('name');
        const email = params.get('email');
        
        if (token && refreshToken && userId) {
            authService.handleOAuthCallback(token, refreshToken, userId, name, email);
            return true;
        }
        return false;
    },
    getClients: async () => {
        const res = await fetch(`${API_URL}/clients`, {
            method: "GET",
            headers: { "Content-Type": "application/json" },
        });
        if (!res.ok) {
            throw new Error('Failed to fetch clients');
        }
        return res.json();
    },
};
export default authService;
